import { LineType, PolygonType, registerOverlay } from 'klinecharts'
import type { OverlayFigure } from 'klinecharts'
import type { FibLevel } from './types'
import { ideaName, markLineLabel, roleOfSlot } from './tradeIdeaVocab'

/**
 * Custom drawing overlays klinecharts doesn't ship (ported from the prototype).
 * Registered once at module load via registerWorkbenchOverlays(). Built-in tools
 * (lines, rays, channels) need no registration; these fill the gaps: boxes, a
 * rich fib, arrows, the Buy/Sell/Exit trade markers and the selection dot.
 */

// colour → rgba at alpha `a`. Accepts hex (#rrggbb, from the levels grid) or an
// rgb/rgba string (from the pill colour picker). Keeps this module React-free.
export function withAlpha(color: string, a: number): string {
  if (color.startsWith('#')) {
    const n = parseInt(color.slice(1), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  const m = color.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  return m ? `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${a})` : color
}

// TradingView's default fib palette + ratios. 0..1 on by default (the classic,
// visible set); the projection levels ship off so the chart stays clean — the
// levels settings popover toggles them.
export const DEFAULT_FIB_LEVELS: FibLevel[] = [
  { value: 0, color: '#787b86', enabled: true },
  { value: 0.236, color: '#f23645', enabled: true },
  { value: 0.382, color: '#ff9800', enabled: true },
  { value: 0.5, color: '#4caf50', enabled: true },
  { value: 0.618, color: '#089981', enabled: true },
  { value: 0.786, color: '#22d3ee', enabled: true },
  { value: 1, color: '#787b86', enabled: true },
  { value: 1.618, color: '#2962ff', enabled: false },
  { value: 2.618, color: '#f23645', enabled: false },
  { value: 3.618, color: '#9c27b0', enabled: false },
  { value: 4.236, color: '#e91e63', enabled: false },
]

interface LevelLineExtendData {
  color?: string
  label?: string
  precision?: number
}

interface FibExtendData {
  levels?: FibLevel[]
  extend?: 'none' | 'left' | 'right' | 'both'
  precision?: number
  labelMode?: 'values' | 'percent'
  connectorColor?: string
  fillColor?: string
  lineSize?: number
}

/** What a trade-idea level carries. See tradeIdeas.ts for the full contract. */
interface TradeMarkExtendData {
  ideaId?: string
  ideaSeq?: number
  ideaColor?: string
  dir?: 'buy' | 'sell'
  slot?: string
  precision?: number
  hidden?: boolean
  /** Transient: this idea is the one in edit mode. Stripped before persisting. */
  active?: boolean
  /**
   * Transient: the pointer is over one of this idea's levels, so the WHOLE idea
   * lights — all its lines together, not just the one under the cursor. A trade
   * idea is one object; hovering any part of it should show you the shape of the
   * plan, which is what the lines drawn together are for.
   */
  lineFocus?: boolean
}

/**
 * How loud a trade idea is when it is not the one being edited.
 *
 * The lines and labels drop right back so several ideas can share a chart
 * without becoming a thicket — but the SHAPES stay lit, because clicking a mark
 * is how you enter its edit mode. A resting idea has to remain findable.
 */
const REST_LINE_ALPHA = 0.18
const REST_SHAPE_ALPHA = 0.92
/** Only reached by a mark saved before ideaColor existed. */
const IDEA_REST_COLOR = '#2970ff'

let registered = false

export function registerWorkbenchOverlays(): void {
  if (registered) return
  registered = true

  // Selection marker — a small hollow circle at a point (TradingView's "you
  // selected this" dot). Locked + non-interactive; the workbench places/removes
  // it on single-click. Moves with the chart because it's anchored to a data point.
  registerOverlay({
    name: 'wbSelectionDot',
    totalStep: 2,
    lock: true,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates }) => {
      if (!coordinates[0]) return []
      return [
        {
          type: 'circle',
          attrs: { x: coordinates[0].x, y: coordinates[0].y, r: 4 },
          styles: { style: 'stroke', color: '#2970ff', borderColor: '#2970ff', borderSize: 1.5 },
        },
      ]
    },
  })

  // Entry→exit rubber band — the dashed cursor-following preview line of the
  // guided Buy/Sell flow. Its endpoint is glued to the cursor, so a pan-drag
  // always STARTS on it; ignoreEvent lets the press fall through to the chart
  // (a plain locked segment would swallow the drag and block panning).
  registerOverlay({
    name: 'rubberBand',
    totalStep: 3,
    lock: true,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates }) => {
      if (coordinates.length < 2) return []
      return [
        {
          type: 'line',
          attrs: { coordinates },
          styles: { style: LineType.Dashed, dashedValue: [2, 3, 8, 3], color: '#9598a1', size: 1 },
          ignoreEvent: true,
        },
      ]
    },
  })

  // Trade-idea level — the entry / stop loss / take profit of a trade idea.
  // One overlay name, three roles: extendData.slot decides the shape and the
  // label, extendData.ideaColor the hue. A full-width dashed rule at the price,
  // an outline shape at the click point, the role in words at the left, and the
  // idea's name beside it so the trader can say "trade idea two" to the AI.
  //
  // Outline, not filled: a filled circle and a filled diamond are the same
  // coloured dot at 5px. Shape is what tells the three roles apart.
  registerOverlay({
    name: 'tradeMark',
    totalStep: 2,
    lock: false,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      if (!coordinates[0]) return []
      const ed = (overlay.extendData ?? {}) as TradeMarkExtendData
      // Hidden from the panel's eye: gone from the canvas AND unhittable —
      // an invisible overlay that still swallows clicks is a trap.
      if (ed.hidden) return []
      const { x, y } = coordinates[0]
      const color = ed.ideaColor ?? IDEA_REST_COLOR
      const role = roleOfSlot(ed.slot) ?? 'entry'
      // Edit mode and hover light the idea the same way: exactly one idea is
      // loud at a time, and either gesture is the trader saying "this one".
      const active = ed.active === true || ed.lineFocus === true

      const shapeColor = active ? color : withAlpha(color, REST_SHAPE_ALPHA)
      const lineColor = active ? color : withAlpha(color, REST_LINE_ALPHA)
      const r = active ? 6 : 4.5
      const bw = active ? 2 : 1.5
      const outline = {
        style: PolygonType.Stroke,
        color: shapeColor,
        borderColor: shapeColor,
        borderSize: bw,
      }

      const shape: OverlayFigure[] =
        role === 'sl'
          ? [
              // Stop loss: a cross. Nothing else on this chart is an X.
              {
                type: 'line',
                attrs: { coordinates: [{ x: x - r, y: y - r }, { x: x + r, y: y + r }] },
                styles: { color: shapeColor, size: bw + 0.5 },
              },
              {
                type: 'line',
                attrs: { coordinates: [{ x: x - r, y: y + r }, { x: x + r, y: y - r }] },
                styles: { color: shapeColor, size: bw + 0.5 },
              },
            ]
          : role === 'tp'
            ? [
                {
                  type: 'polygon',
                  attrs: {
                    coordinates: [
                      { x, y: y - r - 1 },
                      { x: x + r + 1, y },
                      { x, y: y + r + 1 },
                      { x: x - r - 1, y },
                    ],
                  },
                  styles: outline,
                },
              ]
            : [{ type: 'circle', attrs: { x, y, r }, styles: outline }]

      const label = markLineLabel(role, ed.slot ?? role, ed.dir === 'sell' ? 'sell' : 'buy')
      const name = Number.isFinite(ed.ideaSeq) ? ideaName(ed.ideaSeq as number) : null
      const plain = {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderSize: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }

      const figures: OverlayFigure[] = [
        {
          type: 'line',
          attrs: { coordinates: [{ x: 0, y }, { x: bounding?.width ?? 4000, y }] },
          styles: { color: lineColor, size: 1, style: LineType.Dashed, dashedValue: [4, 4] },
        },
        {
          type: 'text',
          attrs: { x: 4, y: y - 2, text: label, align: 'left', baseline: 'bottom' },
          styles: { color: lineColor, size: 11, ...plain },
        },
      ]
      if (name) {
        figures.push({
          type: 'text',
          // klinecharts figures expose no text metrics, so the offset is
          // estimated from the character count (~7px per char at 11px caps).
          attrs: {
            x: 4 + label.length * 7 + 7,
            y: y - 3,
            text: name,
            align: 'left',
            baseline: 'bottom',
          },
          styles: {
            color: withAlpha(color, active ? 0.7 : REST_LINE_ALPHA),
            size: 10,
            ...plain,
          },
        })
      }
      figures.push(
        // Transparent 24×24 grab target — the shapes themselves are too small
        // to hit reliably, and clicking a mark is how you enter its edit mode.
        {
          type: 'polygon',
          attrs: {
            coordinates: [
              { x: x - 12, y: y - 12 },
              { x: x + 12, y: y - 12 },
              { x: x + 12, y: y + 12 },
              { x: x - 12, y: y + 12 },
            ],
          },
          styles: { style: PolygonType.Fill, color: 'rgba(0,0,0,0)' },
        },
        ...shape,
      )
      return figures
    },
    createYAxisFigures: ({ overlay, coordinates, precision }) => {
      const ed = (overlay.extendData ?? {}) as TradeMarkExtendData
      if (ed.hidden || !coordinates[0]) return []
      const v = overlay.points?.[0]?.value
      if (v == null) return []
      const color = ed.ideaColor ?? IDEA_REST_COLOR
      const active = ed.active === true || ed.lineFocus === true
      const prec = precision?.price ?? ed.precision ?? 5
      return [
        {
          type: 'text',
          attrs: {
            x: 0,
            y: coordinates[0].y,
            text: Number(v).toFixed(prec),
            align: 'left',
            baseline: 'middle',
          },
          styles: {
            color: active ? '#ffffff' : withAlpha('#ffffff', 0.55),
            backgroundColor: active ? color : withAlpha(color, 0.22),
            size: 11,
            paddingLeft: 4,
            paddingRight: 4,
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: 2,
          },
        },
      ]
    },
  })

  // Buy entry marker — a green upward triangle sitting under the candle with a
  // "Buy" pill above it. Selectable + draggable; anchored to a data point so it
  // rides the chart on scroll/zoom.
  //
  // LEGACY: the two-click Buy/Sell entry→exit tool that created these is gone
  // (trade ideas replaced it). Kept registered so saved charts still render,
  // and stay selectable, editable and deletable.
  registerOverlay({
    name: 'buyEntry',
    totalStep: 2,
    lock: false,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates }) => {
      if (!coordinates[0]) return []
      const { x, y } = coordinates[0]
      const g = '#089981'
      return [
        { type: 'polygon', attrs: { coordinates: [{ x, y: y + 3 }, { x: x - 6, y: y + 15 }, { x: x + 6, y: y + 15 }] }, styles: { style: 'fill', color: g } },
        { type: 'text', attrs: { x, y: y - 11, text: 'Buy', align: 'center', baseline: 'middle' }, styles: { color: '#ffffff', backgroundColor: g, size: 11, paddingLeft: 5, paddingRight: 5, paddingTop: 2, paddingBottom: 2, borderRadius: 2 } },
      ]
    },
  })

  // Sell entry marker — a red downward triangle above the candle with a "Sell"
  // pill (mirror of buyEntry, for short entries).
  registerOverlay({
    name: 'sellEntry',
    totalStep: 2,
    lock: false,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates }) => {
      if (!coordinates[0]) return []
      const { x, y } = coordinates[0]
      const r = '#f23645'
      return [
        { type: 'polygon', attrs: { coordinates: [{ x, y: y - 3 }, { x: x - 6, y: y - 15 }, { x: x + 6, y: y - 15 }] }, styles: { style: 'fill', color: r } },
        { type: 'text', attrs: { x, y: y - 25, text: 'Sell', align: 'center', baseline: 'middle' }, styles: { color: '#ffffff', backgroundColor: r, size: 11, paddingLeft: 5, paddingRight: 5, paddingTop: 2, paddingBottom: 2, borderRadius: 2 } },
      ]
    },
  })

  // Exit marker — a neutral (monochrome) diamond at the candle with an "Exit"
  // pill, so it reads distinctly from the buy/sell triangles.
  registerOverlay({
    name: 'exitPoint',
    totalStep: 2,
    lock: false,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates }) => {
      if (!coordinates[0]) return []
      const { x, y } = coordinates[0]
      const c = '#9598a1' // neutral grey — exit is not a P&L direction
      return [
        { type: 'polygon', attrs: { coordinates: [{ x, y: y - 13 }, { x: x + 7, y: y - 6 }, { x, y: y + 1 }, { x: x - 7, y: y - 6 }] }, styles: { style: 'fill', color: c } },
        { type: 'text', attrs: { x, y: y - 24, text: 'Exit', align: 'center', baseline: 'middle' }, styles: { color: '#ffffff', backgroundColor: c, size: 11, paddingLeft: 5, paddingRight: 5, paddingTop: 2, paddingBottom: 2, borderRadius: 2 } },
      ]
    },
  })

  // Level line — a full-width dashed horizontal line at a price, for stop /
  // take-profit. Colour + label come from extendData (red "Stop", green
  // "Target"). A price-axis tag mirrors the level.
  registerOverlay({
    name: 'levelLine',
    totalStep: 2,
    lock: false,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ overlay, coordinates, bounding }) => {
      if (!coordinates[0]) return []
      const ed = (overlay.extendData ?? {}) as LevelLineExtendData
      const c = ed.color ?? '#787b86'
      const y = coordinates[0].y
      const figs: OverlayFigure[] = [
        { type: 'line', attrs: { coordinates: [{ x: 0, y }, { x: bounding.width, y }] }, styles: { color: c, size: 1, style: 'dashed', dashedValue: [3, 3] } },
      ]
      if (ed.label) {
        figs.push({ type: 'text', attrs: { x: bounding.width - 6, y: y - 9, text: ed.label, align: 'right', baseline: 'middle' }, styles: { color: c, size: 11, backgroundColor: 'transparent', borderColor: 'transparent', borderSize: 0, paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 } })
      }
      return figs
    },
    createYAxisFigures: ({ overlay, coordinates }) => {
      if (!coordinates[0]) return []
      const ed = (overlay.extendData ?? {}) as LevelLineExtendData
      const c = ed.color ?? '#787b86'
      const prec = ed.precision ?? 5
      return [
        {
          type: 'text',
          attrs: { x: 0, y: coordinates[0].y, text: Number(overlay.points[0]?.value ?? 0).toFixed(prec), align: 'left', baseline: 'middle' },
          styles: { color: '#ffffff', backgroundColor: c, size: 11, paddingLeft: 4, paddingRight: 4, paddingTop: 2, paddingBottom: 2, borderRadius: 2 },
        },
      ]
    },
  })

  // Rectangle / box — two diagonal points draw a filled-stroke rectangle. Fill +
  // border read from the overlay's `polygon` styles so the floating toolbar can
  // customise both live (border colour via pencil, fill colour via paint bucket).
  registerOverlay({
    name: 'rectangle',
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: true,
    needDefaultYAxisFigure: true,
    styles: {
      polygon: {
        style: PolygonType.StrokeFill,
        color: 'rgba(255,214,10,0.10)',
        borderColor: 'rgba(255,214,10,0.9)',
        borderSize: 1,
        borderStyle: LineType.Solid,
      },
    },
    createPointFigures: ({ overlay, coordinates }) => {
      if (coordinates.length < 2) return []
      const [a, b] = coordinates
      const s = overlay.styles?.polygon ?? {}
      return [
        {
          type: 'polygon',
          attrs: {
            coordinates: [
              { x: a.x, y: a.y },
              { x: b.x, y: a.y },
              { x: b.x, y: b.y },
              { x: a.x, y: b.y },
            ],
          },
          styles: {
            style: 'stroke_fill',
            color: s.color ?? 'rgba(255,214,10,0.10)',
            borderColor: s.borderColor ?? 'rgba(255,214,10,0.9)',
            borderSize: s.borderSize ?? 1,
            borderStyle: s.borderStyle ?? 'solid',
          },
        },
      ]
    },
  })

  // Fibonacci retracement — TV-quality: a dashed connector between the two
  // anchors, coloured level lines, translucent bands between them, and a
  // left-side label per level showing ratio + price. The level set / colours /
  // extend live in `extendData` so the levels popover + the pill's Extend can
  // drive them.
  registerOverlay({
    name: 'fibRetracement',
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: true,
    needDefaultYAxisFigure: true,
    createPointFigures: ({ overlay, coordinates, bounding }) => {
      if (coordinates.length < 2) return []
      const ed = (overlay.extendData ?? {}) as FibExtendData
      const levels = (ed.levels ?? DEFAULT_FIB_LEVELS).filter((l) => l.enabled !== false)
      const extend = ed.extend ?? 'none'
      const precision = ed.precision ?? 5
      const labelMode = ed.labelMode ?? 'values'
      const fmtRatio = (v: number) =>
        labelMode === 'percent' ? `${Number((v * 100).toFixed(1))}%` : `${v}`
      const [a, b] = coordinates
      const v0 = overlay.points?.[0]?.value ?? 0
      const v1 = overlay.points?.[1]?.value ?? 0
      const { x: x0, y: y0 } = a
      const { x: x1, y: y1 } = b

      let xL = Math.min(x0, x1)
      let xR = Math.max(x0, x1)
      if (extend === 'right' || extend === 'both') xR = bounding.width
      if (extend === 'left' || extend === 'both') xL = 0
      // Labels normally sit just left of the band (right-aligned, centred on the
      // line). But when extended left the band reaches the page edge — no room
      // outside — so drop them to the far-left edge, left-aligned, above each line.
      const extendsLeft = extend === 'left' || extend === 'both'
      const labelX = extendsLeft ? xL + 4 : Math.min(x0, x1) - 4
      const labelAlign = extendsLeft ? 'left' : 'right'
      const labelBaseline = extendsLeft ? 'bottom' : 'middle'
      const labelDy = extendsLeft ? -3 : 0
      const yOf = (r: number) => y0 + (y1 - y0) * r
      const priceOf = (r: number) => v0 + (v1 - v0) * r

      const figures: OverlayFigure[] = [
        {
          // dashed connector between the two anchors
          type: 'line',
          attrs: { coordinates: [{ x: x0, y: y0 }, { x: x1, y: y1 }] },
          styles: { style: 'dashed', color: ed.connectorColor ?? '#787b86', size: 1, dashedValue: [4, 4] },
        },
      ]
      // translucent bands, coloured by the lower boundary (matches TV)
      for (let i = 0; i < levels.length - 1; i++) {
        const yTop = yOf(levels[i].value)
        const yBot = yOf(levels[i + 1].value)
        figures.push({
          type: 'polygon',
          attrs: { coordinates: [{ x: xL, y: yTop }, { x: xR, y: yTop }, { x: xR, y: yBot }, { x: xL, y: yBot }] },
          // ed.fillColor (pill) overrides the per-level derived fill.
          styles: { style: 'fill', color: ed.fillColor ?? withAlpha(levels[i + 1].color, 0.1) },
        })
      }
      // level lines + left-side labels (ratio + price)
      levels.forEach((l) => {
        const y = yOf(l.value)
        figures.push({
          type: 'line',
          attrs: { coordinates: [{ x: xL, y }, { x: xR, y }] },
          styles: { color: l.color, size: ed.lineSize ?? 1 },
        })
        figures.push({
          type: 'text',
          attrs: { x: labelX, y: y + labelDy, text: `${fmtRatio(l.value)} (${priceOf(l.value).toFixed(precision)})`, align: labelAlign, baseline: labelBaseline },
          // Plain coloured text like TV — kill the engine's default blue label pill.
          styles: {
            color: l.color,
            size: 11,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderSize: 0,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
          },
        })
      })
      return figures
    },
  })

  // Arrow — a two-point segment with a filled arrowhead at the second point.
  // Colour + thickness read from the overlay's `line` styles so the floating
  // toolbar drives both live. Draggable endpoints like a trend line.
  registerOverlay({
    name: 'arrow',
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: true,
    needDefaultYAxisFigure: true,
    styles: { line: { color: '#ffd60a', size: 2 } },
    createPointFigures: ({ overlay, coordinates }) => {
      if (coordinates.length < 2) return []
      const [a, b] = coordinates
      const s = overlay.styles?.line ?? {}
      const color = s.color ?? '#ffd60a'
      const size = s.size ?? 2
      const angle = Math.atan2(b.y - a.y, b.x - a.x)
      const headLen = 9 + size * 2
      const spread = Math.PI / 7
      const h1 = { x: b.x - headLen * Math.cos(angle - spread), y: b.y - headLen * Math.sin(angle - spread) }
      const h2 = { x: b.x - headLen * Math.cos(angle + spread), y: b.y - headLen * Math.sin(angle + spread) }
      return [
        { type: 'line', attrs: { coordinates: [a, b] }, styles: { color, size } },
        { type: 'polygon', attrs: { coordinates: [b, h1, h2] }, styles: { style: 'fill', color } },
      ]
    },
  })
}
